// ./src/pages/private/WorkflowItemsPage/WorkflowItemsPage.tsx
import React, { useState, useCallback, useEffect, useRef } from "react";
import { Stack, Alert, Text, Group } from "@mantine/core";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { IconAlertCircle } from "@tabler/icons-react";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import {
  WorkflowItemTable,
  WorkflowItemEditDrawer,
} from "@/components/features";
import { useWorkflowItems, useWorkflows } from "@/hooks";
import { WorkflowItem, WorkflowItemInput } from "@/utils/api";
import { useApp } from "@/contexts";
import { OPERATION_RIGHTS } from "@/config";
import { ENTITIES } from "@/config/userRoles";

// ---------------------------------------------------------------------------
// Route shapes this page is mounted at (see sitemap):
//
//   /workflows/:workflowId/items                    — list, drawer closed
//   /workflows/:workflowId/items/add                — list, drawer in create mode
//   /workflows/:workflowId/items/:itemId/edit       — list, drawer in edit mode
// ---------------------------------------------------------------------------

export const WorkflowItemsPage: React.FC<PageProps> = (props: PageProps) => {
  const { user, hasRightTo } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ workflowId?: string; itemId?: string }>();

  // ------------------------------------------------------------------
  // Derive UI mode from the URL
  // ------------------------------------------------------------------
  const workflowId = params.workflowId ?? "";
  const itemIdFromUrl = params.itemId;
  const isAddMode = location.pathname.endsWith("/add");
  const isEditMode = !!itemIdFromUrl && location.pathname.endsWith("/edit");
  const isDrawerOpen = isAddMode || isEditMode;

  const canCreate = hasRightTo(ENTITIES.WORKFLOWS, OPERATION_RIGHTS.CREATE);
  const canUpdate = hasRightTo(ENTITIES.WORKFLOWS, OPERATION_RIGHTS.UPDATE);

  // ------------------------------------------------------------------
  // Parent workflow — for header context
  // ------------------------------------------------------------------
  const {
    workflow: parentWorkflow,
    get: getWorkflow,
    loading: loadingWorkflow,
  } = useWorkflows({ autoLoad: false, userId: user?.id });

  useEffect(() => {
    if (workflowId) {
      getWorkflow(workflowId);
    }
  }, [workflowId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ------------------------------------------------------------------
  // Hook — owns all workflow item data and remote operations
  // ------------------------------------------------------------------
  const {
    workflowItems,
    loading,
    isEmpty,
    filters,
    sortBy,
    isCreating,
    isUpdating,
    removingIds,
    lastEditedId,
    lastError,
    list,
    refresh,
    get,
    create,
    update,
    remove,
    clearError,
  } = useWorkflowItems({
    autoLoad: !!workflowId,
    userId: user?.id,
    // Load all items at once — drag-and-drop reordering requires the full
    // list to be present so every position is available as a drop target.
    pageSize: 200,
    initialFilters: workflowId ? { workflowId: [workflowId] } : {},
  });

  // ------------------------------------------------------------------
  // Optimistic reorder state
  //
  // localItems mirrors workflowItems but is updated immediately on drag
  // so the UI responds without waiting for the API round-trip.
  //
  // justReorderedRef prevents the sync effect from overwriting localItems
  // with stale hook state immediately after a successful reorder.
  //
  // savingOrderId identifies the row whose handle shows a spinner.
  // ------------------------------------------------------------------
  const [localItems, setLocalItems] = useState<WorkflowItem[]>([]);
  const [savingOrderId, setSavingOrderId] = useState<string | null>(null);
  const justReorderedRef = useRef(false);

  // Sync localItems from hook state, skipping one cycle after a reorder
  useEffect(() => {
    if (savingOrderId) {
      // A save is still in flight — don't overwrite the optimistic order
      return;
    }
    if (justReorderedRef.current) {
      // Skip one sync after a successful reorder; we already have the right order
      justReorderedRef.current = false;
      return;
    }
    setLocalItems(workflowItems);
  }, [workflowItems, savingOrderId]);

  // ------------------------------------------------------------------
  // Local UI state
  // ------------------------------------------------------------------
  const [itemToDelete, setItemToDelete] = useState<WorkflowItem | null>(null);
  const [editingItem, setEditingItem] = useState<WorkflowItem | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ------------------------------------------------------------------
  // Load the item for edit mode — check localItems first, then fetch
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!isEditMode || !itemIdFromUrl) {
      return;
    }
    const alreadyLoaded = localItems.find((i) => i.id === itemIdFromUrl);
    if (alreadyLoaded) {
      setEditingItem(alreadyLoaded);
      return;
    }
    get(itemIdFromUrl);
  }, [isEditMode, itemIdFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync editingItem when localItems updates
  useEffect(() => {
    if (!isEditMode || !itemIdFromUrl) {
      setEditingItem(null);
      return;
    }
    const found = localItems.find((i) => i.id === itemIdFromUrl);
    if (found) {
      setEditingItem(found);
    }
  }, [isEditMode, itemIdFromUrl, localItems]);

  // ------------------------------------------------------------------
  // Drag-and-drop reorder — optimistic pattern from FormFieldsEditModal
  //
  // Algorithm:
  //   1. Compute new orderNo as the midpoint between neighbours.
  //   2. Add a small timestamp decimal to break ties between rapid
  //      reorders of the same neighbours.
  //   3. Apply optimistically to localItems immediately.
  //   4. Fire update() in the background.
  //   5. On error, revert localItems to the latest server state.
  // ------------------------------------------------------------------
  const handleOrderChange = useCallback(
    async (
      draggedItem: WorkflowItem,
      itemBefore: WorkflowItem | undefined,
      itemAfter: WorkflowItem | undefined,
      newLocalOrder: WorkflowItem[],
    ) => {
      if (!draggedItem.id || !canUpdate) return;

      // Step 1 — optimistically apply the new display order
      setLocalItems(newLocalOrder);
      setSavingOrderId(draggedItem.id);

      try {
        // Step 2 — compute the target orderNo
        let newOrderNo: number;

        if (itemBefore && itemAfter) {
          const before = itemBefore.orderNo ?? 0;
          const after = itemAfter.orderNo ?? 0;
          newOrderNo = before + (after - before) / 2;
        } else if (itemAfter) {
          // Dropped at the top
          newOrderNo = (itemAfter.orderNo ?? 1000) / 2;
        } else if (itemBefore) {
          // Dropped at the bottom
          newOrderNo = (itemBefore.orderNo ?? 0) + 1000;
        } else {
          // Only item in the list
          newOrderNo = 1000;
        }

        // Step 3 — add timestamp decimal to avoid collisions on rapid reorders
        const timestampDecimal = (Date.now() % 1000) / 1000;
        const finalOrderNo = newOrderNo + timestampDecimal;

        // Step 4 — reflect the computed orderNo in localItems so the
        // displayed order number stays consistent with what was saved
        setLocalItems((prev) =>
          prev.map((i) =>
            i.id === draggedItem.id ? { ...i, orderNo: finalOrderNo } : i,
          ),
        );

        // Step 5 — persist
        await update(draggedItem.id, { orderNo: finalOrderNo });

        // Mark that we just reordered so the sync effect skips one cycle
        justReorderedRef.current = true;
      } catch {
        // Revert to the latest server state on any error
        justReorderedRef.current = false;
        setLocalItems(workflowItems);
      } finally {
        setSavingOrderId(null);
      }
    },
    [update, canUpdate, workflowItems],
  );

  // ------------------------------------------------------------------
  // Search
  // ------------------------------------------------------------------
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      const newFilters = { ...filters };
      if (value.trim()) {
        newFilters.searchKeyword = value.trim();
      } else {
        delete newFilters.searchKeyword;
      }
      list(newFilters, { page: 1, pageSize: 200 }, sortBy, true);
    },
    [filters, sortBy, list],
  );

  // ------------------------------------------------------------------
  // Sort
  // ------------------------------------------------------------------
  //   const handleSortChange = useCallback(
  //     (newSortBy: typeof sortBy) => {
  //       list(filters, { page: 1, pageSize: 200 }, newSortBy, true);
  //     },
  //     [filters, list],
  //   );

  // ------------------------------------------------------------------
  // Refresh
  // ------------------------------------------------------------------
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // ------------------------------------------------------------------
  // Back
  // ------------------------------------------------------------------
  const handleBack = useCallback(() => {
    navigate("/workflows");
  }, [navigate]);

  // ------------------------------------------------------------------
  // Navigation — drawer driven by URL
  // ------------------------------------------------------------------
  const handleAddItem = useCallback(() => {
    clearError();
    navigate(`/workflows/${workflowId}/items/add`);
  }, [navigate, workflowId, clearError]);

  const handleEditItem = useCallback(
    (item: WorkflowItem) => {
      clearError();
      if (item.id) {
        navigate(`/workflows/${workflowId}/items/${item.id}/edit`);
      }
    },
    [navigate, workflowId, clearError],
  );

  const handleDrawerClose = useCallback(() => {
    clearError();
    setEditingItem(null);
    navigate(`/workflows/${workflowId}/items`);
  }, [navigate, workflowId, clearError]);

  // ------------------------------------------------------------------
  // Remove
  // ------------------------------------------------------------------
  const handleRemoveItemClick = useCallback((item: WorkflowItem) => {
    setDeleteError(null);
    setItemToDelete(item);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setItemToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!itemToDelete?.id) return;

    const result = await remove(itemToDelete.id);

    if (result.didSucceed()) {
      setItemToDelete(null);
      setDeleteError(null);
      if (itemIdFromUrl === itemToDelete.id) {
        navigate(`/workflows/${workflowId}/items`);
      }
    } else {
      const msg =
        result.getErrorSummary() ||
        "Could not delete the workflow item. Please try again.";
      setDeleteError(msg);
      setItemToDelete(null);
    }
  }, [itemToDelete, remove, itemIdFromUrl, workflowId, navigate]);

  // ------------------------------------------------------------------
  // Create
  // ------------------------------------------------------------------
  const handleCreateItem = useCallback(
    async (params: WorkflowItemInput) => {
      const result = await create(params);
      if (result.didSucceed()) {
        navigate(`/workflows/${workflowId}/items`);
      }
      return result;
    },
    [create, navigate, workflowId],
  );

  // ------------------------------------------------------------------
  // Update
  // ------------------------------------------------------------------
  const handleUpdateItem = useCallback(
    async (id: string, params: WorkflowItemInput) => {
      const result = await update(id, params);
      if (result.didSucceed()) {
        navigate(`/workflows/${workflowId}/items`);
      }
      return result;
    },
    [update, navigate, workflowId],
  );

  // ------------------------------------------------------------------
  // Error scoping
  // ------------------------------------------------------------------
  const listError =
    !isDrawerOpen && lastError ? lastError.getErrorSummary() : null;

  // ------------------------------------------------------------------
  // Page title
  // ------------------------------------------------------------------
  const pageTitle = loadingWorkflow
    ? "Workflow Items"
    : parentWorkflow?.workflowName
      ? `${parentWorkflow.workflowName} — Items`
      : "Workflow Items";

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <AppButton
                  key="back"
                  variant="secondary"
                  iconName="ArrowLeft"
                  onClick={handleBack}
                  disabled={loading}
                >
                  Workflows
                </AppButton>,
                <AppButton
                  key="add-item"
                  iconName="Plus"
                  onClick={handleAddItem}
                  disabled={isCreating || loading || !canCreate}
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={isCreating || loading}
                />,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search items..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Page heading */}
        {parentWorkflow?.workflowName && (
          <Group gap="xs">
            <Text size="lg" fw={600}>
              {pageTitle}
            </Text>
          </Group>
        )}

        {/* List-level error */}
        {listError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title="Could not load workflow items"
            withCloseButton
            onClose={clearError}
          >
            <Text size="sm">{listError}</Text>
          </Alert>
        )}

        {/* Delete error */}
        {deleteError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title="Could not delete workflow item"
            withCloseButton
            onClose={() => setDeleteError(null)}
          >
            <Text size="sm">{deleteError}</Text>
          </Alert>
        )}

        {/* Table — uses localItems for optimistic reorder display */}
        <WorkflowItemTable
          workflowItems={localItems}
          loading={loading}
          onEditWorkflowItem={handleEditItem}
          onRemoveWorkflowItem={handleRemoveItemClick}
          removingIds={removingIds}
          lastEditedId={lastEditedId}
          onReorder={canUpdate ? handleOrderChange : undefined}
          savingOrderId={savingOrderId}
          canReorder={canUpdate}
        />

        {/* Empty state */}
        <EmptyState
          display={isEmpty && !loading}
          title="No workflow items yet"
          description="Add items to define the conditions and actions for this workflow"
          actionIcon="Plus"
          actionLabel="Add Item"
          onAction={handleAddItem}
        />
      </Stack>

      {/* Create / Edit drawer */}
      <WorkflowItemEditDrawer
        opened={isDrawerOpen}
        onClose={handleDrawerClose}
        workflowItem={editingItem}
        workflowId={workflowId}
        onCreateWorkflowItem={handleCreateItem}
        onUpdateWorkflowItem={handleUpdateItem}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={isDrawerOpen ? lastError : null}
        onClearError={clearError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={!!itemToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Workflow Item"
        message="Are you sure you want to delete this workflow item?"
        details="This action cannot be undone."
        confirmInProgress={
          !!itemToDelete?.id && removingIds.has(itemToDelete.id)
        }
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
